<template>
  <div class="hk-page bg-light-page">

    <!-- Header -->
    <div class="hk-header d-flex align-items-center">
      <button class="hk-back-btn d-flex align-items-center justify-content-center" type="button" @click="$router.back()">
        <ArrowLeft :size="20" :stroke-width="2.5" />
      </button>
      <h1 class="heading-6-1 mb-0">Housekeeping</h1>
    </div>

    <!-- Screen 1: List -->
    <div v-if="screen === 'list'" class="hk-scroll">

      <div v-for="group in GROUPS" :key="group.label" class="hk-group px-3">
        <p class="body-3 mb-2">{{ group.label }}</p>
        <div class="hk-group-card">
          <div
            v-for="(item, i) in group.items"
            :key="item.id"
            :class="['hk-item d-flex align-items-center', { 'hk-item--bordered': i > 0 }, { 'hk-item--selected': item.type === 'toggle' && isSelected(item.id) }]"
            @click="item.type === 'toggle' ? toggleItem(item.id) : null"
          >
            <div class="flex-grow-1 overflow-hidden mr-2">
              <span class="hk-item-name">{{ item.name }}</span>
              <span v-if="item.charge" class="hk-charge-badge ml-2">Charges may apply</span>
            </div>
            <div v-if="item.type === 'toggle'" :class="['hk-check flex-shrink-0 d-flex align-items-center justify-content-center', { 'hk-check--on': isSelected(item.id) }]">
              <Check v-if="isSelected(item.id)" :size="12" :stroke-width="3.5" />
            </div>
            <div v-else class="hk-stepper d-flex align-items-center flex-shrink-0" @click.stop>
              <button
                class="hk-step-btn d-flex align-items-center justify-content-center"
                type="button"
                :disabled="quantities[item.id] === 0"
                @click="decrement(item.id)"
              >
                <Minus :size="13" :stroke-width="2.5" />
              </button>
              <span class="hk-step-val font-lato">{{ quantities[item.id] }}</span>
              <button
                class="hk-step-btn d-flex align-items-center justify-content-center"
                type="button"
                :disabled="quantities[item.id] >= item.max"
                @click="increment(item.id, item.max)"
              >
                <Plus :size="13" :stroke-width="2.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="px-3 pb-2 mt-2">
        <textarea
          v-model="note"
          class="hk-note w-100"
          rows="3"
          placeholder="Additional notes (optional), e.g. please come after 3 PM"
        ></textarea>
      </div>

      <div style="height: 96px"></div>
    </div>

    <!-- Sticky submit bar -->
    <div v-if="screen === 'list'" class="hk-sticky-bar">
      <button
        class="hk-submit-btn w-100"
        type="button"
        :disabled="selectedCount === 0"
        @click="showReview = true"
      >
        Submit Request{{ selectedCount > 0 ? ` (${selectedCount} item${selectedCount > 1 ? 's' : ''})` : '' }}
      </button>
    </div>

    <!-- Screen 3: Success -->
    <transition name="hk-fade">
      <div v-if="screen === 'success'" class="hk-success d-flex flex-column align-items-center px-4">
        <div class="hk-success-icon d-flex align-items-center justify-content-center mb-4">
          <CheckCircle :size="56" :stroke-width="1.5" />
        </div>
        <h2 class="hk-success-title text-center mb-2">Request Submitted!</h2>
        <p class="hk-success-sub text-center mb-4">Our housekeeping team will take care of your request shortly.</p>

        <div v-if="submittedData" class="hk-success-info w-100 mb-4">
          <div class="hk-info-row d-flex justify-content-between align-items-center">
            <span class="hk-info-label">Invoice</span>
            <span class="hk-info-value font-lato">{{ submittedData.invoice }}</span>
          </div>
          <div v-if="submittedData.ga_reservation && submittedData.ga_reservation.room" class="hk-info-row d-flex justify-content-between align-items-center">
            <span class="hk-info-label">Room</span>
            <span class="hk-info-value">{{ submittedData.ga_reservation.room.name }}</span>
          </div>
          <div v-if="submittedData.ga_reservation && submittedData.ga_reservation.room_type" class="hk-info-row d-flex justify-content-between align-items-center">
            <span class="hk-info-label">Room Type</span>
            <span class="hk-info-value">{{ submittedData.ga_reservation.room_type.name }}</span>
          </div>
          <div v-if="submittedData.ga_reservation && submittedData.ga_reservation.booked_by" class="hk-info-row d-flex justify-content-between align-items-center">
            <span class="hk-info-label">Guest</span>
            <span class="hk-info-value">{{ submittedData.ga_reservation.booked_by.name }}</span>
          </div>
          <div class="hk-info-row d-flex justify-content-between align-items-center">
            <span class="hk-info-label">Status</span>
            <span class="hk-status-badge">{{ submittedData.service_status || submittedData.status }}</span>
          </div>
        </div>

        <div class="hk-success-summary w-100 mb-5">
          <p class="hk-summary-heading mb-3">Items requested</p>
          <div
            v-for="item in submittedItems"
            :key="item.id"
            class="d-flex justify-content-between align-items-center hk-summary-row"
          >
            <span class="hk-summary-name">{{ item.name }}</span>
            <span class="hk-summary-qty">{{ item.qty > 1 ? '× ' + item.qty : '✓' }}</span>
          </div>
        </div>

        <button class="hk-submit-btn w-100 mb-3" type="button" @click="backToHome">
          Back to Home
        </button>
        <button class="hk-outline-btn w-100" type="button">
          View My Requests
        </button>
      </div>
    </transition>

    <!-- Screen 2: Bottom Sheet Review -->
    <transition name="hk-sheet-anim">
      <div v-if="showReview" class="hk-backdrop" @click.self="showReview = false">
        <div class="hk-sheet">
          <div class="hk-sheet-handle mx-auto mb-3"></div>
          <button class="hk-sheet-close d-flex align-items-center justify-content-center" type="button" @click="showReview = false">
            <X :size="18" :stroke-width="2.5" />
          </button>

          <h3 class="heading-6-1 mb-4">Review Your Request</h3>

          <div class="hk-sheet-list mb-3">
            <div
              v-for="item in selectedItems"
              :key="item.id"
              class="d-flex justify-content-between align-items-center hk-sheet-row"
            >
              <span class="hk-sheet-item-name">
                {{ item.name }}<span v-if="item.charge" class="hk-charge-star"> *</span>
              </span>
              <span class="hk-sheet-item-qty">× {{ item.qty }}</span>
            </div>
          </div>

          <p v-if="hasChargeItems" class="hk-disclaimer mb-3">
            Selected items marked with * may result in additional charges to your room bill.
          </p>

          <div v-if="note.trim()" class="hk-sheet-note mb-4">
            <p class="hk-note-label mb-1">Your note</p>
            <p class="mb-0 hk-note-text">{{ note }}</p>
          </div>

          <p v-if="submitError" class="hk-submit-error mb-3">{{ submitError }}</p>

          <button class="hk-submit-btn w-100 mb-3" type="button" :disabled="isSubmitting" @click="confirmRequest">
            {{ isSubmitting ? 'Submitting...' : 'Confirm Request' }}
          </button>
          <button class="hk-outline-btn w-100" type="button" :disabled="isSubmitting" @click="showReview = false">
            Cancel
          </button>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { ArrowLeft, Check, Minus, Plus, X, CheckCircle } from 'lucide-vue'
import { post } from '@/services/index.js'

const GROUPS = [
  {
    label: 'CLEANING',
    items: [
      { id: 'make-up-room',       name: 'Make-up Room',      type: 'toggle' },
      { id: 'turndown-service',   name: 'Turndown Service',  type: 'toggle' },
      { id: 'bathroom-cleaning',  name: 'Bathroom Cleaning', type: 'toggle' },
      { id: 'trash-pickup',   name: 'Trash Pickup',      type: 'toggle' },
      { id: 'deep-cleaning',  name: 'Deep Cleaning',     type: 'toggle', charge: true },
    ],
  },
  {
    label: 'LINEN & BEDDING',
    items: [
      { id: 'extra-pillow',    name: 'Extra Pillow',    type: 'qty', max: 5 },
      { id: 'extra-blanket',   name: 'Extra Blanket',   type: 'qty', max: 5 },
      { id: 'extra-towel',     name: 'Extra Towel',     type: 'qty', max: 5 },
      { id: 'change-bedsheet', name: 'Change Bedsheet', type: 'toggle' },
    ],
  },
  {
    label: 'AMENITIES',
    items: [
      { id: 'refill-mineral-water', name: 'Refill Mineral Water', type: 'qty', max: 5 },
      { id: 'baby-crib',     name: 'Baby Crib',            type: 'toggle' },
      { id: 'iron-iron-board', name: 'Iron & Iron Board',   type: 'toggle' },
      { id: 'hair-dryer',    name: 'Hair Dryer',           type: 'toggle' },
    ],
  },
]

const ALL_ITEMS = GROUPS.flatMap(g => g.items)

function initSelected() {
  return Object.fromEntries(ALL_ITEMS.filter(i => i.type === 'toggle').map(i => [i.id, false]))
}

function initQuantities() {
  return Object.fromEntries(ALL_ITEMS.filter(i => i.type === 'qty').map(i => [i.id, 0]))
}

export default {
  name: 'ConciergeHousekeeping',

  components: { ArrowLeft, Check, Minus, Plus, X, CheckCircle },

  metaInfo() {
    return {
      title: 'Housekeeping',
      titleTemplate: this.getMetaTitleTemplate(),
    }
  },

  data() {
    return {
      GROUPS,
      screen:         'list',
      showReview:     false,
      selected:       initSelected(),
      quantities:     initQuantities(),
      note:           '',
      submittedItems: [],
      submittedData:  null,
      isSubmitting:   false,
      submitError:    '',
    }
  },

  computed: {
    selectedItems() {
      const result = []
      for (const group of GROUPS) {
        for (const item of group.items) {
          if (item.type === 'toggle' && this.selected[item.id]) {
            result.push({ ...item, qty: 1 })
          } else if (item.type === 'qty' && this.quantities[item.id] > 0) {
            result.push({ ...item, qty: this.quantities[item.id] })
          }
        }
      }
      return result
    },

    selectedCount() {
      return this.selectedItems.length
    },

    hasChargeItems() {
      return this.selectedItems.some(i => i.charge)
    },
  },

  mounted() {
    var auth = this.getConciergeHomeAuth()
    if (auth.reservation_status && auth.reservation_status !== 'CHECKIN') {
      this.$router.replace({ name: 'ConciergeMainPage', query: { toast: 'Available after check-in' } })
    }
  },

  methods: {
    ...mapGetters(['getMerchant', 'getMetaTitleTemplate', 'getConciergeHomeAuth']),

    toggleItem(id) {
      this.$set(this.selected, id, !this.selected[id])
    },

    isSelected(id) {
      return this.selected[id] || false
    },

    increment(id, max) {
      const cur = this.quantities[id] || 0
      if (cur < max) this.$set(this.quantities, id, cur + 1)
    },

    decrement(id) {
      const cur = this.quantities[id] || 0
      if (cur > 0) this.$set(this.quantities, id, cur - 1)
    },

    confirmRequest() {
      if (this.isSubmitting) return
      this.isSubmitting = true
      this.submitError = ''

      var auth = this.getConciergeHomeAuth()
      var toggleItems = {}
      ALL_ITEMS.filter(function(i) { return i.type === 'toggle' }).forEach(function(i) {
        toggleItems[i.id] = false
      })
      this.selectedItems.forEach(function(item) {
        toggleItems[item.id] = true
      })

      var qtyItems = {}
      ALL_ITEMS.filter(function(i) { return i.type === 'qty' }).forEach(function(i) {
        qtyItems[i.id] = 0
      })
      this.selectedItems.filter(function(i) { return i.type === 'qty' }).forEach(function(item) {
        qtyItems[item.id] = item.qty
      })

      var params = Object.assign(
        {
          reservation_id: auth.reservation_id,
          merchant_id:    this.getMerchant().id,
          module_slug:    'housekeeping',
          notes:          this.note,
        },
        toggleItems,
        qtyItems
      )
      
      post('CONCIERGE', 'service/tx-inquiry', params, { type: 'concierge_token_direct', token: auth.token })
        .then(function(response) {
          var res = response.data
          if (res.success) {
            this.submittedItems = [...this.selectedItems]
            this.submittedData = res.data || null
            this.showReview = false
            this.$nextTick(function() {
              this.screen = 'success'
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }.bind(this))
          } else {
            this.submitError = res.message || 'Failed to submit request.'
          }
          this.isSubmitting = false
        }.bind(this))
        .catch(function() {
          this.submitError = 'Something went wrong. Please try again.'
          this.isSubmitting = false
        }.bind(this))
    },

    backToHome() {
      this.$router.push({ name: 'ConciergeMainPage' })
    },
  },
}
</script>

<style scoped>
/* ── Page & layout ─────────────────────────── */
.hk-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* ── Header ────────────────────────────────── */
.hk-header {
  padding: 14px 16px;
  background: #fff;
  border-bottom: 1px solid #E8EEF3;
  position: sticky;
  top: 0;
  z-index: 10;
  gap: 12px;
}

.hk-back-btn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1.5px solid #E0E8EF;
  background: #F5F7FA;
  color: #1C2B36;
  cursor: pointer;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
}

/* ── Scroll area ───────────────────────────── */
.hk-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-top: 24px;
}

/* ── Group ─────────────────────────────────── */
.hk-group {
  margin-bottom: 24px;
}

.hk-group-label {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #7B8A96;
  margin-bottom: 8px;
}

.hk-group-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #E0E8EF;
  box-shadow: 0 1px 2px rgba(15, 30, 50, 0.04), 0 4px 14px rgba(15, 30, 50, 0.05);
  overflow: hidden;
}

/* ── Item row ──────────────────────────────── */
.hk-item {
  padding: 14px 16px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.1s;
}

.hk-item--bordered {
  border-top: 1px solid #F0F4F7;
}

.hk-item--selected {
  background: #F2F8FC;
}

.hk-item-name {
  font-size: 14.5px;
  font-weight: 600;
  color: #1C2B36;
  line-height: 1.3;
}

.hk-charge-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  color: #3B6FA8;
  background: #EAF1FB;
  border: 1px solid #B8D0EF;
  border-radius: 6px;
  padding: 2px 7px;
  vertical-align: middle;
  white-space: nowrap;
}

/* ── Checkbox ──────────────────────────────── */
.hk-check {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  border: 2px solid #CBD5DF;
  background: #F5F7FA;
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s;
}

.hk-check--on {
  background: var(--merchant-primary-color, #2B5F7A);
  border-color: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
}

/* ── Stepper ───────────────────────────────── */
.hk-stepper {
  gap: 0;
  border: 1.5px solid #E0E8EF;
  border-radius: 10px;
  overflow: hidden;
}

.hk-step-btn {
  width: 34px;
  height: 34px;
  background: #F5F7FA;
  border: none;
  color: #1C2B36;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.1s;
}

.hk-step-btn:disabled {
  color: #BFC8D0;
  cursor: default;
}

.hk-step-btn:not(:disabled):active {
  background: #E6EEF4;
}

.hk-step-val {
  min-width: 32px;
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  color: #1C2B36;
  background: #fff;
  line-height: 34px;
}

/* ── Note textarea ─────────────────────────── */
.hk-note {
  border: 1.5px solid #E0E8EF;
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 13.5px;
  color: #1C2B36;
  background: #fff;
  resize: none;
  font-family: inherit;
  line-height: 1.5;
  outline: none;
  transition: border-color 0.15s;
}

.hk-note:focus {
  border-color: var(--merchant-primary-color, #2B5F7A);
}

/* ── Sticky bar ────────────────────────────── */
.hk-sticky-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
  padding: 14px 16px 22px;
  border-top: 1px solid #E8EEF3;
  z-index: 20;
}

/* ── Buttons ───────────────────────────────── */
.hk-submit-btn {
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
  border: none;
  border-radius: 14px;
  padding: 15px 20px;
  font-size: 15px;
  cursor: pointer;
  font-family: inherit;
  letter-spacing: 0.2px;
  -webkit-tap-highlight-color: transparent;
  transition: filter 0.12s;
  font-family: Nunito-Bold !important;
}

.hk-submit-btn:disabled {
  background: #C8D4DC;
  cursor: default;
}

.hk-submit-btn:not(:disabled):active {
  filter: brightness(0.9);
}

.hk-outline-btn {
  background: transparent;
  color: var(--merchant-primary-color, #2B5F7A);
  border: 2px solid var(--merchant-primary-color, #2B5F7A);
  border-radius: 14px;
  padding: 13px 20px;
  font-size: 15px;
  cursor: pointer;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.12s;
  font-family: Nunito-Bold !important;
}

.hk-outline-btn:active {
  background: rgba(43, 95, 122, 0.07);
}

/* ── Screen 3: Success ─────────────────────── */
.hk-success {
  flex: 1;
  padding-top: 64px;
  padding-bottom: 40px;
}

.hk-success-icon {
  width: 96px;
  height: 96px;
  border-radius: 999px;
  background: #E6F5EC;
  color: #1A8F44;
}

.hk-success-title {
  font-size: 24px;
  font-weight: 900;
  color: #1C2B36;
  letter-spacing: -0.4px;
}

.hk-success-sub {
  font-size: 14px;
  color: #6B7886;
  line-height: 1.5;
}

.hk-success-info {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #E0E8EF;
  padding: 4px 16px;
  box-shadow: 0 2px 8px rgba(15, 30, 50, 0.05);
}

.hk-info-row {
  padding: 11px 0;
  border-bottom: 1px solid #F0F4F7;
  gap: 12px;
}

.hk-info-row:last-child {
  border-bottom: none;
}

.hk-info-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #7B8A96;
  flex-shrink: 0;
}

.hk-info-value {
  font-size: 13.5px;
  font-weight: 700;
  color: #1C2B36;
  text-align: right;
}

.hk-status-badge {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #B45309;
  background: #FEF3C7;
  border: 1px solid #FDE68A;
  border-radius: 8px;
  padding: 3px 10px;
}

.hk-success-summary {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #E0E8EF;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(15, 30, 50, 0.05);
}

.hk-summary-heading {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: #7B8A96;
}

.hk-summary-row {
  padding: 8px 0;
  border-top: 1px solid #F0F4F7;
}

.hk-summary-name {
  font-size: 14px;
  font-weight: 600;
  color: #1C2B36;
}

.hk-summary-qty {
  font-size: 13px;
  font-weight: 700;
  color: var(--merchant-primary-color, #2B5F7A);
}

/* ── Bottom Sheet ──────────────────────────── */
.hk-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 20, 30, 0.55);
  z-index: 1050;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.hk-sheet {
  background: #fff;
  border-radius: 24px 24px 0 0;
  padding: 16px 20px 36px;
  position: relative;
  max-height: 85vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.hk-sheet-handle {
  width: 36px;
  height: 4px;
  border-radius: 999px;
  background: #D0D9E2;
  margin-bottom: 8px;
}

.hk-sheet-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: #F0F4F7;
  border: none;
  color: #6B7886;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.hk-sheet-title {
  font-size: 20px;
  font-weight: 900;
  color: #1C2B36;
  letter-spacing: -0.3px;
}

.hk-sheet-list {
  border: 1px solid #E8EEF3;
  border-radius: 14px;
  overflow: hidden;
}

.hk-sheet-row {
  padding: 12px 14px;
}

.hk-sheet-row + .hk-sheet-row {
  border-top: 1px solid #F0F4F7;
}

.hk-sheet-item-name {
  font-size: 14px;
  font-weight: 600;
  color: #1C2B36;
}

.hk-charge-star {
  color: #3B6FA8;
  font-weight: 800;
}

.hk-sheet-item-qty {
  font-size: 13px;
  font-weight: 700;
  color: var(--merchant-primary-color, #2B5F7A);
  flex-shrink: 0;
  margin-left: 8px;
}

.hk-submit-error {
  font-size: 13px;
  color: #C0392B;
  background: #FEF0EE;
  border: 1px solid #F5C6C0;
  border-radius: 10px;
  padding: 10px 12px;
  line-height: 1.5;
}

.hk-disclaimer {
  font-size: 12px;
  color: #3B6FA8;
  background: #EAF1FB;
  border: 1px solid #B8D0EF;
  border-radius: 10px;
  padding: 10px 12px;
  line-height: 1.5;
}

.hk-sheet-note {
  background: #F5F7FA;
  border-radius: 12px;
  padding: 12px 14px;
}

.hk-note-label {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #7B8A96;
}

.hk-note-text {
  font-size: 13.5px;
  color: #1C2B36;
  line-height: 1.5;
}

/* ── Transitions ───────────────────────────── */
.hk-fade-enter-active  { transition: opacity 0.35s ease, transform 0.35s cubic-bezier(0.22, 1, 0.36, 1); }
.hk-fade-enter         { opacity: 0; transform: translateY(24px); }

.hk-sheet-anim-enter-active, .hk-sheet-anim-leave-active {
  transition: opacity 0.25s;
}
.hk-sheet-anim-enter .hk-sheet,
.hk-sheet-anim-leave-to .hk-sheet {
  transform: translateY(100%);
}
.hk-sheet-anim-enter-active .hk-sheet,
.hk-sheet-anim-leave-active .hk-sheet {
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}
.hk-sheet-anim-enter, .hk-sheet-anim-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/pages/hotel/concierge/Housekeeping.vue